import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Avatar, Box, Divider, Group, Stack, Tooltip } from '@mantine/core'
import { AppModal, AppSkeleton, AppTextarea } from '../../app/common/ui'
import { toast } from 'react-toastify'
import {
  IoThumbsUp,
  IoThumbsUpOutline,
  IoTrashOutline,
  IoEyeOffOutline,
  IoArrowBack,
  IoChatbubbleOutline,
  IoImageOutline,
  IoCreateOutline,
  IoCloseOutline
} from 'react-icons/io5'
import agent from '../../app/api/agent'
import type { CachePostCommentDto, CachePostDetailDto } from '../../app/models/cache'
import { AppButton, AppCard, AppText, AmmoScoreBadge, ImageLightbox, PostTypeBadge } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { timeAgo } from './cacheUi'
import CachePostForm from './CachePostForm'

const PAGE_MAX = 740

function AuthorRow ({
  username,
  displayName,
  image,
  ammoTier,
  ammoScore,
  createdAt,
  size = 'md'
}: {
  username: string | null;
  displayName: string | null;
  image: string | null;
  ammoTier: string;
  ammoScore: number;
  createdAt: string;
  size?: 'sm' | 'md';
}) {
  const label = displayName || username || 'Unknown'
  const avatarSize = size === 'sm' ? 22 : 28
  const nameSize = size === 'sm' ? 'xs' : 'sm'

  return (
    <Group spacing={8} align="center" noWrap>
      <Avatar
        radius="xl"
        size={avatarSize}
        src={image || undefined}
        color="ember"
        component={username ? Link : undefined}
        to={username ? `/profiles/${username}` : undefined}
        sx={{ flexShrink: 0, textDecoration: 'none', fontWeight: 700 }}
      >
        {label.slice(0, 2).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Group spacing={6} noWrap align="center">
          <AppText
            size={nameSize}
            weight={600}
            component={username ? Link : undefined}
            to={username ? `/profiles/${username}` : undefined}
            sx={{ textDecoration: 'none', color: 'var(--gs-text)', '&:hover': username ? { color: 'var(--gs-accent)' } : {} }}
          >
            {label}
          </AppText>
          {ammoTier && (
            <AmmoScoreBadge tier={ammoTier} score={ammoScore} size="sm" />
          )}
        </Group>
        {username && (
          <AppText size="xs" color="dimmed">@{username} · {timeAgo(createdAt)}</AppText>
        )}
      </Box>
    </Group>
  )
}

function updateCommentInTree (
  comments: CachePostCommentDto[],
  updated: CachePostCommentDto
): CachePostCommentDto[] {
  return comments.map((c) => {
    if (c.id === updated.id) return updated
    if (c.replies.length > 0) {
      return { ...c, replies: updateCommentInTree(c.replies, updated) }
    }
    return c
  })
}

export default observer(function CachePostPage () {
  const { id, postId } = useParams<{ id: string; postId: string }>()
  const { userStore } = useStore()
  const [post, setPost] = useState<CachePostDetailDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [voting, setVoting] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  // Comment voting
  const [votingCommentId, setVotingCommentId] = useState<string | null>(null)

  // Top-level comment composer
  const [commentText, setCommentText] = useState('')
  const [commentFile, setCommentFile] = useState<File | null>(null)
  const [commentPreview, setCommentPreview] = useState<string | null>(null)
  const [commentSending, setCommentSending] = useState(false)
  const commentImageRef = useRef<HTMLInputElement>(null)

  // Reply composer
  const [replyingToId, setReplyingToId] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [replyFile, setReplyFile] = useState<File | null>(null)
  const [replyPreview, setReplyPreview] = useState<string | null>(null)
  const [replySending, setReplySending] = useState(false)
  const replyImageRef = useRef<HTMLInputElement>(null)

  const loadPost = useCallback(() => {
    if (!id || !postId) return
    setLoading(true)
    agent.Caches.getPost(id, postId)
      .then(setPost)
      .catch(() => setPost(null))
      .finally(() => setLoading(false))
  }, [id, postId])

  useEffect(() => { loadPost() }, [loadPost])

  const handleVotePost = async () => {
    if (!post || !id || !postId || !userStore.isLoggedIn) return
    setVoting(true)
    try {
      const updated = await agent.Caches.voteOnPost(id, postId)
      setPost((prev) => prev ? { ...prev, voteScore: updated.voteScore, currentUserVote: updated.currentUserVote } : prev)
    } catch {
      /* interceptor */
    } finally {
      setVoting(false)
    }
  }

  const handleVoteComment = async (commentId: string) => {
    if (!id || !postId || !userStore.isLoggedIn) return
    setVotingCommentId(commentId)
    try {
      const updated = await agent.Caches.voteOnComment(id, postId, commentId)
      setPost((prev) => {
        if (!prev) return prev
        return { ...prev, comments: updateCommentInTree(prev.comments, updated) }
      })
    } catch {
      /* interceptor */
    } finally {
      setVotingCommentId(null)
    }
  }

  const handleDelete = async () => {
    if (!id || !postId) return
    try {
      await agent.Caches.deletePost(id, postId)
      toast.success('Post deleted')
      window.history.back()
    } catch {
      /* interceptor */
    }
  }

  const handleHide = async () => {
    if (!id || !postId) return
    try {
      await agent.Caches.hidePost(id, postId)
      toast.success('Post hidden')
      window.history.back()
    } catch {
      /* interceptor */
    }
  }

  const handleCommentImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (commentPreview?.startsWith('blob:')) URL.revokeObjectURL(commentPreview)
    setCommentFile(file)
    setCommentPreview(URL.createObjectURL(file))
  }

  const removeCommentImage = () => {
    if (commentPreview?.startsWith('blob:')) URL.revokeObjectURL(commentPreview)
    setCommentFile(null)
    setCommentPreview(null)
    if (commentImageRef.current) commentImageRef.current.value = ''
  }

  const handleSubmitComment = async () => {
    if (!id || !postId || (!commentText.trim() && !commentFile)) return
    setCommentSending(true)
    try {
      let imageUrl: string | null = null
      if (commentFile) {
        const res = await agent.Caches.uploadCommentImage(id, postId, commentFile)
        imageUrl = res.url
      }
      await agent.Caches.addComment(id, postId, commentText.trim() || null, null, imageUrl)
      setCommentText('')
      removeCommentImage()
      loadPost()
    } catch {
      /* interceptor */
    } finally {
      setCommentSending(false)
    }
  }

  const handleReplyImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (replyPreview?.startsWith('blob:')) URL.revokeObjectURL(replyPreview)
    setReplyFile(file)
    setReplyPreview(URL.createObjectURL(file))
  }

  const removeReplyImage = () => {
    if (replyPreview?.startsWith('blob:')) URL.revokeObjectURL(replyPreview)
    setReplyFile(null)
    setReplyPreview(null)
    if (replyImageRef.current) replyImageRef.current.value = ''
  }

  const openReply = (commentId: string) => {
    setReplyingToId(commentId)
    setReplyText('')
    removeReplyImage()
  }

  const cancelReply = () => {
    setReplyingToId(null)
    setReplyText('')
    removeReplyImage()
  }

  const handleSubmitReply = async (parentCommentId: string) => {
    if (!id || !postId || (!replyText.trim() && !replyFile)) return
    setReplySending(true)
    try {
      let imageUrl: string | null = null
      if (replyFile) {
        const res = await agent.Caches.uploadCommentImage(id, postId, replyFile)
        imageUrl = res.url
      }
      await agent.Caches.addComment(id, postId, replyText.trim() || null, parentCommentId, imageUrl)
      cancelReply()
      loadPost()
    } catch {
      /* interceptor */
    } finally {
      setReplySending(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ padding: '16px 24px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
        <AppSkeleton height={20} width={80} mb="md" radius="sm" />
        <AppSkeleton height={180} mb="sm" radius={8} />
        <AppSkeleton height={28} mb="sm" radius="sm" />
        <AppSkeleton height={14} width={220} mb="lg" radius="sm" />
        <AppSkeleton height={80} mb="lg" radius="sm" />
      </Box>
    )
  }

  if (!post) {
    return (
      <Box sx={{ padding: '24px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
        <AppButton component={Link} to={`/caches/${id}`} variant="subtle" size="xs" mb="md" leftIcon={<IoArrowBack size={13} />}>Back to cache</AppButton>
        <AppCard sx={{ textAlign: 'center', padding: 32 }}>
          <AppText weight={600} mb={4}>Post not found</AppText>
          <AppText size="sm" color="dimmed">It may have been removed or belongs to a private cache.</AppText>
        </AppCard>
      </Box>
    )
  }

  const currentUsername = userStore.user?.username
  const isAuthor = !!currentUsername && post.authorUsername === currentUsername
  const isAdmin = userStore.user?.isAdmin ?? false
  const hasVoted = (post.currentUserVote ?? 0) > 0

  const totalComments = post.comments.reduce((n, c) => n + 1 + c.replies.length, 0)

  return (
    <Box sx={{ padding: '16px 24px 56px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
      <AppButton component={Link} to={`/caches/${id}`} variant="subtle" size="xs" mb="sm" leftIcon={<IoArrowBack size={13} />}>Back to cache</AppButton>

      {/* Cover image */}
      {post.coverImageUrl && (
        <Box
          sx={{ borderRadius: 10, overflow: 'hidden', mb: 'md', cursor: 'pointer', aspectRatio: '16 / 7' }}
          onClick={() => setLightboxUrl(post.coverImageUrl)}
        >
          <img
            src={post.coverImageUrl}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            alt={post.title}
          />
        </Box>
      )}

      {/* Post card */}
      <AppCard padding="lg" mb="lg">
        {/* Type badge + actions */}
        <Group position="apart" align="center" mb="sm" noWrap>
          <PostTypeBadge type={post.postType as any} />
          <Group spacing={6} sx={{ flexShrink: 0 }}>
            {userStore.isLoggedIn && (
              <AppButton
                size="xs"
                variant={hasVoted ? 'filled' : 'outline'}
                color={hasVoted ? 'ember' : 'gray'}
                leftIcon={hasVoted ? <IoThumbsUp size={13} /> : <IoThumbsUpOutline size={13} />}
                loading={voting}
                onClick={() => void handleVotePost()}
              >
                {post.voteScore}
              </AppButton>
            )}
            {!userStore.isLoggedIn && post.voteScore !== 0 && (
              <AppText size="xs" color="dimmed">▲ {post.voteScore}</AppText>
            )}
            {isAuthor && (
              <Tooltip label="Edit post" withArrow>
                <AppButton size="xs" variant="subtle" color="gray" onClick={() => setEditOpen(true)} aria-label="Edit post">
                  <IoCreateOutline size={15} />
                </AppButton>
              </Tooltip>
            )}
            {isAuthor && (
              <Tooltip label="Delete post" withArrow>
                <AppButton size="xs" variant="subtle" color="red" onClick={() => void handleDelete()} aria-label="Delete post">
                  <IoTrashOutline size={14} />
                </AppButton>
              </Tooltip>
            )}
            {!isAuthor && isAdmin && (
              <Tooltip label="Hide post" withArrow>
                <AppButton size="xs" variant="subtle" color="orange" onClick={() => void handleHide()} aria-label="Hide post">
                  <IoEyeOffOutline size={14} />
                </AppButton>
              </Tooltip>
            )}
          </Group>
        </Group>

        {/* Title */}
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 24, lineHeight: 1.2, mb: 12 }}>
          {post.title}
        </AppText>

        {/* Author */}
        <Box mb={post.body ? 'md' : 0}>
          <AuthorRow
            username={post.authorUsername}
            displayName={post.authorDisplayName}
            image={post.authorImage}
            ammoTier={post.authorAmmoTier}
            ammoScore={post.authorAmmoScore}
            createdAt={post.createdAt}
          />
        </Box>

        {/* Body */}
        {post.body && (
          <AppText
            size="sm"
            mt="md"
            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, borderTop: '1px solid var(--gs-border)', paddingTop: 12 }}
          >
            {post.body}
          </AppText>
        )}
      </AppCard>

      {/* Comments header */}
      <Group spacing={6} mb="sm" align="center">
        <IoChatbubbleOutline size={15} color="var(--gs-muted)" />
        <AppText size="xs" weight={700} color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: '0.8px' }}>
          {totalComments === 1 ? '1 Comment' : `${totalComments} Comments`}
        </AppText>
      </Group>

      {/* Comment list */}
      <Stack spacing="sm" mb="lg">
        {post.comments.length === 0
          ? (
          <AppText size="sm" color="dimmed" py="sm">No comments yet. Be the first.</AppText>
            )
          : post.comments.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              depth={0}
              cacheId={id}
              postId={postId}
              currentUsername={currentUsername}
              isAdmin={isAdmin}
              isLoggedIn={userStore.isLoggedIn}
              votingCommentId={votingCommentId}
              replyingToId={replyingToId}
              replyText={replyText}
              replyPreview={replyPreview}
              replySending={replySending}
              replyImageRef={replyImageRef}
              onVote={handleVoteComment}
              onOpenReply={openReply}
              onCancelReply={cancelReply}
              onReplyTextChange={setReplyText}
              onReplyImageSelect={handleReplyImageSelect}
              onRemoveReplyImage={removeReplyImage}
              onSubmitReply={handleSubmitReply}
              onImageClick={setLightboxUrl}
            />
          ))}
      </Stack>

      <Divider mb="md" />

      {/* Comment composer */}
      {userStore.isLoggedIn
        ? (
        <Box>
          <AppText size="xs" weight={600} color="dimmed" mb={8} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Add a comment
          </AppText>
          <AppTextarea
            placeholder="Share your thoughts…"
            value={commentText}
            onChange={(e) => setCommentText(e.currentTarget.value)}
            minRows={2}
            autosize
            maxRows={10}
            mb="xs"
          />
          {commentPreview && (
            <Box mb="xs" sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8 }}>
              <Box
                sx={{ cursor: 'pointer', borderRadius: 6, overflow: 'hidden' }}
                onClick={() => setLightboxUrl(commentPreview)}
              >
                <img src={commentPreview} style={{ height: 72, display: 'block', borderRadius: 6 }} alt="preview" />
              </Box>
              <AppButton size="xs" variant="subtle" color="red" onClick={removeCommentImage} compact>
                <IoCloseOutline size={14} />
              </AppButton>
            </Box>
          )}
          <Group position="apart">
            <AppButton
              size="xs"
              variant="subtle"
              color="gray"
              leftIcon={<IoImageOutline size={13} />}
              onClick={() => commentImageRef.current?.click()}
              type="button"
            >
              Add image
            </AppButton>
            <AppButton
              size="sm"
              color="ember"
              loading={commentSending}
              disabled={!commentText.trim() && !commentFile}
              onClick={() => void handleSubmitComment()}
            >
              Comment
            </AppButton>
          </Group>
          <input
            ref={commentImageRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={handleCommentImageSelect}
          />
        </Box>
          )
        : (
        <AppButton component={Link} to="/login" variant="light" size="sm">Log in to comment</AppButton>
          )}

      {/* Edit post modal */}
      <AppModal
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        title={<AppText weight={700}>Edit post</AppText>}
        size="lg"
        centered
      >
        <CachePostForm
          cacheId={id}
          postId={postId}
          initialValues={{
            title: post.title,
            body: post.body,
            postType: post.postType,
            coverImageUrl: post.coverImageUrl,
          }}
          onSuccess={(updated) => {
            setPost((prev) => prev ? { ...prev, ...updated } : prev)
            setEditOpen(false)
            toast.success('Post updated')
          }}
          onCancel={() => setEditOpen(false)}
        />
      </AppModal>

      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} alt={post.title} />
    </Box>
  )
})

interface CommentNodeProps {
  comment: CachePostCommentDto;
  depth: number;
  cacheId: string;
  postId: string;
  currentUsername: string | undefined;
  isAdmin: boolean;
  isLoggedIn: boolean;
  votingCommentId: string | null;
  replyingToId: string | null;
  replyText: string;
  replyPreview: string | null;
  replySending: boolean;
  replyImageRef: React.RefObject<HTMLInputElement>;
  onVote: (id: string) => void;
  onOpenReply: (id: string) => void;
  onCancelReply: () => void;
  onReplyTextChange: (text: string) => void;
  onReplyImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveReplyImage: () => void;
  onSubmitReply: (parentId: string) => void;
  onImageClick: (url: string) => void;
}

function CommentNode ({
  comment,
  depth,
  cacheId,
  postId,
  currentUsername,
  isAdmin,
  isLoggedIn,
  votingCommentId,
  replyingToId,
  replyText,
  replyPreview,
  replySending,
  replyImageRef,
  onVote,
  onOpenReply,
  onCancelReply,
  onReplyTextChange,
  onReplyImageSelect,
  onRemoveReplyImage,
  onSubmitReply,
  onImageClick
}: CommentNodeProps) {
  const hasVoted = (comment.currentUserVote ?? 0) > 0
  const isThisReply = replyingToId === comment.id

  if (comment.isHidden && !isAdmin) return null

  return (
    <Box
      sx={{
        paddingLeft: depth > 0 ? 16 : 0,
        borderLeft: depth > 0 ? '2px solid var(--gs-border)' : undefined,
        marginLeft: depth > 0 ? 8 : 0,
      }}
    >
      <AppCard
        padding="sm"
        sx={{
          background: depth > 0 ? 'var(--gs-surface2)' : 'var(--gs-surface)',
          opacity: comment.isHidden ? 0.6 : 1,
        }}
      >
        {comment.isHidden && (
          <Group spacing={4} mb={4}>
            <IoEyeOffOutline size={12} color="var(--gs-muted)" />
            <AppText size="xs" color="dimmed" sx={{ fontStyle: 'italic' }}>
              Hidden{comment.hiddenReason ? ` — ${comment.hiddenReason}` : ''}
            </AppText>
          </Group>
        )}

        <Group position="apart" align="flex-start" mb={comment.body || comment.imageUrl ? 8 : 0} noWrap>
          <AuthorRow
            username={comment.authorUsername}
            displayName={comment.authorDisplayName}
            image={comment.authorImage}
            ammoTier={comment.authorAmmoTier}
            ammoScore={comment.authorAmmoScore}
            createdAt={comment.createdAt}
            size="sm"
          />
          {/* Vote */}
          <Group spacing={4} sx={{ flexShrink: 0 }}>
            {isLoggedIn
              ? (
              <AppButton
                size="xs"
                variant={hasVoted ? 'filled' : 'subtle'}
                color={hasVoted ? 'ember' : 'gray'}
                leftIcon={hasVoted ? <IoThumbsUp size={11} /> : <IoThumbsUpOutline size={11} />}
                loading={votingCommentId === comment.id}
                onClick={() => onVote(comment.id)}
                compact
              >
                {comment.voteScore}
              </AppButton>
                )
              : comment.voteScore > 0
                ? <AppText size="xs" color="dimmed">▲ {comment.voteScore}</AppText>
                : null}
          </Group>
        </Group>

        {/* Body */}
        {comment.body && (
          <AppText size="sm" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, mb: comment.imageUrl ? 8 : 4 }}>
            {comment.body}
          </AppText>
        )}

        {/* Image */}
        {comment.imageUrl && (
          <Box
            mb={8}
            sx={{ cursor: 'pointer', display: 'inline-block', borderRadius: 6, overflow: 'hidden' }}
            onClick={() => onImageClick(comment.imageUrl!)}
          >
            <img
              src={comment.imageUrl}
              style={{ maxWidth: '100%', maxHeight: 240, display: 'block', borderRadius: 6 }}
              alt="comment image"
            />
          </Box>
        )}

        {/* Actions */}
        {isLoggedIn && depth === 0 && (
          <Group spacing={4} mt={4}>
            <AppButton
              size="xs"
              variant="subtle"
              color="gray"
              leftIcon={<IoChatbubbleOutline size={11} />}
              onClick={() => isThisReply ? onCancelReply() : onOpenReply(comment.id)}
              compact
            >
              {isThisReply ? 'Cancel' : 'Reply'}
            </AppButton>
          </Group>
        )}
      </AppCard>

      {/* Inline reply composer */}
      {isThisReply && (
        <Box mt={8} sx={{ paddingLeft: 8 }}>
          <AppTextarea
            placeholder={`Replying to @${comment.authorUsername ?? 'user'}…`}
            value={replyText}
            onChange={(e) => onReplyTextChange(e.currentTarget.value)}
            minRows={2}
            autosize
            maxRows={8}
            mb="xs"
            size="sm"
            autoFocus
          />
          {replyPreview && (
            <Box mb="xs" sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6 }}>
              <Box
                sx={{ cursor: 'pointer', borderRadius: 4, overflow: 'hidden' }}
                onClick={() => onImageClick(replyPreview)}
              >
                <img src={replyPreview} style={{ height: 60, display: 'block', borderRadius: 4 }} alt="preview" />
              </Box>
              <AppButton size="xs" variant="subtle" color="red" onClick={onRemoveReplyImage} compact>
                <IoCloseOutline size={13} />
              </AppButton>
            </Box>
          )}
          <Group position="apart">
            <AppButton
              size="xs"
              variant="subtle"
              color="gray"
              leftIcon={<IoImageOutline size={12} />}
              onClick={() => replyImageRef.current?.click()}
              type="button"
            >
              Image
            </AppButton>
            <Group spacing="xs">
              <AppButton size="xs" variant="subtle" onClick={onCancelReply}>Cancel</AppButton>
              <AppButton
                size="xs"
                color="ember"
                loading={replySending}
                disabled={!replyText.trim() && !replyPreview}
                onClick={() => onSubmitReply(comment.id)}
              >
                Reply
              </AppButton>
            </Group>
          </Group>
          <input
            ref={replyImageRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={onReplyImageSelect}
          />
        </Box>
      )}

      {/* Nested replies */}
      {comment.replies.length > 0 && (
        <Stack spacing={8} mt={8}>
          {comment.replies.map((r) => (
            <CommentNode
              key={r.id}
              comment={r}
              depth={1}
              cacheId={cacheId}
              postId={postId}
              currentUsername={currentUsername}
              isAdmin={isAdmin}
              isLoggedIn={isLoggedIn}
              votingCommentId={votingCommentId}
              replyingToId={replyingToId}
              replyText={replyText}
              replyPreview={replyPreview}
              replySending={replySending}
              replyImageRef={replyImageRef}
              onVote={onVote}
              onOpenReply={onOpenReply}
              onCancelReply={onCancelReply}
              onReplyTextChange={onReplyTextChange}
              onReplyImageSelect={onReplyImageSelect}
              onRemoveReplyImage={onRemoveReplyImage}
              onSubmitReply={onSubmitReply}
              onImageClick={onImageClick}
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}
